// War Room: verdict-grouped ASIN scorecard vs. Marketplace Pros, with per-ASIN
// action tracking. Consumes Task 4's /api/war-room/* endpoints via ApiClient
// (matches other pages' auth/token handling — see ApiClient.js interceptors).
import React, { useCallback, useEffect, useState } from 'react';
import ApiClient from '../../utils/ApiClient';
import SummaryStrip from './SummaryStrip';
import ScorecardTable from './ScorecardTable';
import WarRoomTabs from './WarRoomTabs';

const ALL_VERDICTS = ['ENFORCE-MAP', 'DEFEND', 'CONTEST', 'PRICE', 'FLIP', 'SPREAD', 'HOLD', 'REVIEW'];

function WarRoom() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    const [filterMpOnly, setFilterMpOnly] = useState(true);
    const [verdictFilter, setVerdictFilter] = useState(new Set(ALL_VERDICTS));
    const [search, setSearch] = useState('');

    const fetchScorecard = useCallback(async () => {
        try {
            const response = await ApiClient.get('/api/war-room/scorecard');
            setData(response.data);
            setError(null);
        } catch (err) {
            console.error('Error fetching war room scorecard:', err);
            setError('Failed to load the scorecard. Please try again.');
        }
    }, []);

    useEffect(() => {
        setLoading(true);
        fetchScorecard().finally(() => setLoading(false));
    }, [fetchScorecard]);

    const handleRefresh = async () => {
        setRefreshing(true);
        setError(null);
        try {
            await ApiClient.post('/api/war-room/refresh');
            await fetchScorecard();
        } catch (err) {
            console.error('Error refreshing war room scorecard:', err);
            setError('Refresh failed. Please try again.');
        } finally {
            setRefreshing(false);
        }
    };

    const handleDownload = async () => {
        try {
            const response = await ApiClient.get('/api/war-room/export', { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            const dateStr = data?.scorecardDate || new Date().toISOString().split('T')[0];
            link.href = url;
            link.download = `asin_scorecard_${dateStr}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading war room export:', err);
            setError('Download failed. Please try again.');
        }
    };

    const toggleVerdict = (verdict) => {
        setVerdictFilter((prev) => {
            const next = new Set(prev);
            if (next.has(verdict)) {
                next.delete(verdict);
            } else {
                next.add(verdict);
            }
            return next;
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <svg className="animate-spin h-8 w-8 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
            </div>
        );
    }

    const rows = data?.rows || [];

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">War Room</h1>
                <p className="text-sm text-gray-500">ASIN-level buy-box scorecard vs. Marketplace Pros</p>
            </div>

            <WarRoomTabs active="Scorecard" />

            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
                    {error}
                </div>
            )}

            <SummaryStrip
                summary={data?.summary}
                scorecardDate={data?.scorecardDate}
                refreshing={refreshing}
                onRefresh={handleRefresh}
                onDownload={handleDownload}
            />

            <ScorecardTable
                rows={rows}
                filterMpOnly={filterMpOnly}
                onToggleMpOnly={() => setFilterMpOnly((v) => !v)}
                verdictFilter={verdictFilter}
                onToggleVerdict={toggleVerdict}
                search={search}
                onSearchChange={setSearch}
                onActionSaved={fetchScorecard}
            />
        </div>
    );
}

export default WarRoom;
